/**
 * Page /upload — interface "Nouveau AO".
 *
 * Zone de chat libre. L'utilisateur peut :
 *   - Écrire un message libre → l'agent répond (mais cette conversation
 *     éphémère n'a pas vocation à être reprise — la "vraie" session est
 *     attachée à chaque AO créé).
 *   - Cliquer 📎 + sélectionner un PDF → POST /api/tenders → navigate
 *     vers /tenders/:id où la conversation tender persistante démarre.
 *
 * Pas de sidebar conversations ni de lien historique : la session vit
 * dans /tenders/:id, pas ici.
 *
 * Mode "bulk ZIP" toujours disponible via un toggle discret pour les power
 * users qui veulent uploader plusieurs AO d'un coup.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { api } from '../lib/api-client';
import { AgentChat } from '../components/AgentChat';

type BulkResult = {
  total: number;
  results: Array<{
    filename: string;
    outcome: 'created' | 'duplicate' | 'skipped' | 'failed';
    tenderId?: string;
    reason?: string;
  }>;
};

export function Upload() {
  const navigate = useNavigate();
  const [showBulk, setShowBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  /**
   * Handler appele par AgentChat quand l'utilisateur envoie un fichier.
   * Cree le tender via POST /api/tenders, transmet le message texte (s'il
   * existe) en query param ?msg= que TenderDetail picke comme initialDraft
   * de la conversation tender, puis navigate.
   */
  async function handleAttachFile(file: File, message: string): Promise<void> {
    if (file.type !== 'application/pdf') {
      throw new Error("Pour l'instant seuls les PDF sont supportes pour l'AO. (Excel/Word a venir.)");
    }
    if (file.size > 50 * 1024 * 1024) {
      throw new Error('Fichier trop gros (max 50 MB).');
    }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('nom', file.name.replace(/\.pdf$/i, ''));

    const tender = await api.upload<{ tenderId: string }>('/api/tenders', fd);

    const qs = message.trim() ? `?msg=${encodeURIComponent(message.trim())}` : '';
    navigate(`/tenders/${tender.tenderId}${qs}`);
  }

  /** Upload de plusieurs PDFs d'un coup : une création par fichier (chaque
   *  création enqueue déjà le traitement côté API). */
  async function handleMultiPdf(files: File[]) {
    setBulkLoading(true);
    setBulkError(null);
    setBulkResult(null);
    const results: BulkResult['results'] = [];
    for (const file of files) {
      if (file.type !== 'application/pdf') {
        results.push({ filename: file.name, outcome: 'skipped', reason: 'pas un PDF' });
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('nom', file.name.replace(/\.pdf$/i, ''));
        const t = await api.upload<{ tenderId: string }>('/api/tenders', fd);
        results.push({ filename: file.name, outcome: 'created', tenderId: t.tenderId });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'échec';
        results.push({
          filename: file.name,
          outcome: /déjà|duplicate/i.test(msg) ? 'duplicate' : 'failed',
          reason: msg,
        });
      }
    }
    setBulkResult({ total: files.length, results });
    setBulkLoading(false);
  }

  async function handleBulk(zipFile: File) {
    setBulkLoading(true);
    setBulkError(null);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', zipFile);
      const data = await api.upload<BulkResult>('/api/tenders/bulk', fd);
      setBulkResult(data);
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Erreur upload');
    } finally {
      setBulkLoading(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <div className="tc-label mb-2">tenders / new</div>
        <h1 className="text-2xl font-display tracking-tight">Nouvel AO</h1>
        <p className="text-sm text-steel-500 mt-1">
          Décris ton besoin ou attache directement le PDF de l'AO. Une fois le PDF envoyé, le
          pipeline démarre et tu pourras continuer la conversation depuis la page de l'AO —
          chaque AO a sa propre session sauvegardée.
        </p>
      </div>

      <AgentChat
        contextKind="general"
        height="calc(100vh - 280px)"
        emptyHint="Pose une question, ou attache un PDF d'AO via le bouton trombone pour lancer le traitement automatique."
        inputPlaceholder="Décris ton AO, pose une question, ou clique sur le trombone pour joindre un PDF…"
        onAttachFile={handleAttachFile}
        attachAccept="application/pdf"
      />

      {/* Mode bulk discret (power users) */}
      <div className="mt-6 text-center">
        <button
          onClick={() => setShowBulk(!showBulk)}
          className="text-2xs font-mono uppercase tracking-widest text-steel-500 hover:text-steel-100"
        >
          {showBulk ? '× masquer' : '+ mode batch (plusieurs PDFs ou ZIP)'}
        </button>
      </div>

      {showBulk && (
        <div className="tc-panel p-5 mt-3">
          <div className="tc-label mb-2">mode batch</div>
          <p className="text-sm text-steel-300 mb-4">
            Upload une archive <code className="text-amber-500">.zip</code> contenant plusieurs
            PDFs. Chaque PDF devient un AO indépendant. Limite : 20 AO/heure/utilisateur.
            Doublons (fichiers identiques) ignorés automatiquement.
          </p>
          <div className="flex flex-wrap gap-2">
            <label className="tc-btn-primary cursor-pointer inline-block">
              {bulkLoading ? 'processing...' : '+ plusieurs PDFs'}
              <input
                type="file"
                accept="application/pdf"
                multiple
                className="hidden"
                disabled={bulkLoading}
                onChange={(e) => {
                  const files = e.target.files ? Array.from(e.target.files) : [];
                  if (files.length) handleMultiPdf(files);
                  e.target.value = '';
                }}
              />
            </label>
            <label className="tc-btn-secondary cursor-pointer inline-block">
              {bulkLoading ? 'processing...' : '+ ZIP'}
              <input
                type="file"
                accept=".zip,application/zip"
                className="hidden"
                disabled={bulkLoading}
                onChange={(e) => e.target.files?.[0] && handleBulk(e.target.files[0])}
              />
            </label>
          </div>

          {bulkError && (
            <div className="border border-red-900/60 bg-red-900/20 text-red-300 px-4 py-3 mt-4 text-sm font-mono">
              ERROR · {bulkError}
            </div>
          )}

          {bulkResult && (
            <div className="mt-4">
              <div className="tc-label mb-3">{bulkResult.total} files processed</div>
              <ul className="border border-ink-700">
                {bulkResult.results.map((r, i) => (
                  <li
                    key={i}
                    className="px-3 py-2 flex items-center justify-between gap-3 border-b border-ink-700/50 last:border-b-0"
                  >
                    <span className="font-mono text-xs truncate max-w-xs text-steel-300">
                      {r.filename}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      {r.outcome === 'created' ? (
                        <a
                          href={`/tenders/${r.tenderId}`}
                          className="text-2xs font-mono uppercase tracking-widest text-amber-500 hover:text-amber-400 inline-flex items-center gap-1"
                        >
                          <Check aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                          <span>created</span>
                          <ArrowRight aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                        </a>
                      ) : (
                        <span
                          className={`tc-status ${
                            r.outcome === 'duplicate'
                              ? 'tc-status-warn'
                              : r.outcome === 'skipped'
                                ? 'tc-status-neutral'
                                : 'tc-status-error'
                          }`}
                        >
                          {r.outcome}
                        </span>
                      )}
                      {r.reason && (
                        <span className="text-2xs text-steel-500 font-mono">{r.reason}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
